import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  IconButton,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Radio,
  FormHelperText,
  FormGroup,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
// Logic-related imports removed - logic now managed globally through Logic Rules button

const countryOptions = [
  { code: 'US', label: 'United States', dialCode: '+1' },
  { code: 'GB', label: 'United Kingdom', dialCode: '+44' },
  { code: 'IN', label: 'India', dialCode: '+91' },
  { code: 'CA', label: 'Canada', dialCode: '+1' },
  { code: 'AU', label: 'Australia', dialCode: '+61' },
  { code: 'DE', label: 'Germany', dialCode: '+49' },
  { code: 'FR', label: 'France', dialCode: '+33' },
  { code: 'IT', label: 'Italy', dialCode: '+39' },
  { code: 'ES', label: 'Spain', dialCode: '+34' },
  { code: 'BR', label: 'Brazil', dialCode: '+55' },
  { code: 'JP', label: 'Japan', dialCode: '+81' },
  { code: 'KR', label: 'South Korea', dialCode: '+82' },
  { code: 'CN', label: 'China', dialCode: '+86' },
  { code: 'RU', label: 'Russia', dialCode: '+7' },
  { code: 'MX', label: 'Mexico', dialCode: '+52' },
  // Add more countries as needed
];

const QuestionOptions = ({ question, onChange, onClose, allQuestions, surveyId, userId }) => {
  const [activeTab, setActiveTab] = useState('general'); // Only general tab now exists

  const handleChange = (field, value) => {
    // Special handling for image-choice questions
    if (question.type === 'image-choice') {
      // For image-choice questions, preserve the options array and store config separately
      if (['caption', 'placeholder', 'defaultValue', 'errorMessage', 'minLength', 'maxLength', 'minValue', 'maxValue'].includes(field)) {
        // Store UI configuration separately from the image options array
        onChange(field, value);
      } else if (field === 'config') {
        onChange('config', value);
      } else if (field === 'validation') {
        onChange('validation', value);
      } else {
        // For all other fields, pass the value directly
        onChange(field, value);
      }
    } else {
      // Store UI-related configuration in the options object for non-image-choice questions
      if (['caption', 'placeholder', 'defaultValue', 'errorMessage', 'minLength', 'maxLength', 'minValue', 'maxValue'].includes(field)) {
        // Call onChange with the field name directly - the parent will handle storing it in options
        onChange(field, value);
      } else if (field === 'randomize') {
        onChange(field, value);
        const options = [...(question.choices || [])];

        if (value) {
          // Store original order before shuffling
          onChange('originalOrder', [...options]);

          // Find Other option index if it exists
          const otherIndex = options.findIndex(opt => opt === 'Other');
          const hasOther = otherIndex !== -1;

          // Remove Other option temporarily if it exists
          const optionsToShuffle = hasOther ?
            [...options.slice(0, otherIndex), ...options.slice(otherIndex + 1)] :
            [...options];

          // Shuffle the remaining options
          for (let i = optionsToShuffle.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [optionsToShuffle[i], optionsToShuffle[j]] =
              [optionsToShuffle[j], optionsToShuffle[i]];
          }

          // Add Other back at its original position if it existed
          if (hasOther) {
            optionsToShuffle.splice(otherIndex, 0, 'Other');
          }

          onChange('choices', optionsToShuffle);
        } else {
          // Restore original order
          if (question.originalOrder) {
            onChange('choices', question.originalOrder);
          }
        }
      } else if (field === 'config') {
        // For config objects, call onChange directly - parent will handle storing in options
        onChange('config', value);
      } else if (field === 'validation') {
        // For validation objects, call onChange directly - parent will handle storing in options
        onChange('validation', value);
      } else if (field === 'enabledFields') {
        // For address fields configuration, call onChange directly - parent will handle storing in options
        onChange('enabledFields', value);
      } else if (field === 'fieldErrors') {
        // For field-specific error messages, call onChange directly - parent will handle storing in options
        onChange('fieldErrors', value);
      } else {
        // For all other fields, pass the value directly
        onChange(field, value);
      }
    }
  };

  const handleBulkAdd = (count) => {
    const newCount = parseInt(count);
    if (!isNaN(newCount) && newCount > 0) {
      const currentOptions = [...(question.choices || [])];
      const newOptions = [];

      for (let i = 0; i < newCount; i++) {
        newOptions.push(""); // Add empty strings instead of pre-filled text
      }

      onChange('choices', [...currentOptions, ...newOptions]);
    }
  };

  // Get values from options object with fallbacks - special handling for image-choice
  const getOptionValue = (field, defaultValue = '') => {
    // For image-choice questions, the options is an array, so get config values from direct properties
    if (question.type === 'image-choice') {
      return question[field] !== undefined ? question[field] : defaultValue;
    }
    // For other question types, use the standard options object approach
    return question.options?.[field] !== undefined ? question.options[field] :
      question[field] !== undefined ? question[field] : defaultValue;
  };

  const commonOptions = (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Label
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Click text on page to modify
        </Typography>
      </Box>
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Caption"
          size="small"
          value={getOptionValue('caption')}
          onChange={(e) => handleChange('caption', e.target.value)}
          helperText="Add helper text below the question"
        />
      </Box>

      {/* Show placeholder for all question types except date and rating */}
      {!['date', 'rating', 'matrix', 'nps', 'website', 'address', 'likert-scale', 'dropdown', 'multiple-choice', 'range-scale', 'image-choice', 'datetime', 'time','checkboxes','file-upload','ranking'].includes(question.type) && (
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Placeholder"
            size="small"
            value={getOptionValue('placeholder')}
            onChange={(e) => handleChange('placeholder', e.target.value)}
            helperText="Text that appears in the input field"
          />
        </Box>
      )}

      {/* Hide default value for date, rating, and phone question types */}
      {!['date', 'rating', 'phone', 'matrix', 'nps', 'website', 'email', 'address', 'likert-scale', 'dropdown', 'multiple-choice', 'range-scale', 'image-choice', 'datetime', 'time','checkboxes','file-upload','ranking'].includes(question.type) && (
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Default Value"
            size="small"
            value={getOptionValue('defaultValue')}
            onChange={(e) => handleChange('defaultValue', e.target.value)}
          />
        </Box>
      )}

      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={question.required || false}
              onChange={(e) => onChange('required', e.target.checked)}
            />
          }
          label="Required"
        />
      </Box>
    </>
  );

  const textOptions = (
    <>
      <Box sx={{ mb: 3 }}>
        <Box
          onClick={() => onChange('showValidation', !question.showValidation)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            p: 1,
            border: '1px solid rgba(0, 0, 0, 0.23)',
            borderRadius: '4px',
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.87)'
            }
          }}
        >
          <Typography>Validation</Typography>
          <Typography
            sx={{
              transform: question.showValidation ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.3s'
            }}
          >
            ^
          </Typography>
        </Box>

        {question.showValidation && (
          <Box sx={{ mt: 2 }}>
            {question.type === 'number' ? (
              <>
                <TextField
                  fullWidth
                  label="Min Value"
                  type="number"
                  size="small"
                  value={getOptionValue('minValue')}
                  onChange={(e) => handleChange('minValue', e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Max Value"
                  type="number"
                  size="small"
                  value={getOptionValue('maxValue')}
                  onChange={(e) => handleChange('maxValue', e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Error message"
                  size="small"
                  value={getOptionValue('errorMessage')}
                  onChange={(e) => handleChange('errorMessage', e.target.value)}
                  helperText="Message to show when validation fails"
                />
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Min length"
                  type="number"
                  size="small"
                  value={getOptionValue('minLength')}
                  onChange={(e) => handleChange('minLength', e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Max length"
                  type="number"
                  size="small"
                  value={getOptionValue('maxLength')}
                  onChange={(e) => handleChange('maxLength', e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Error message"
                  size="small"
                  value={getOptionValue('errorMessage')}
                  onChange={(e) => handleChange('errorMessage', e.target.value)}
                />
              </>
            )}
          </Box>
        )}
      </Box>
    </>
  );

  const multipleChoiceOptions = (
    <Box sx={{ mb: 3 }}>
      <TextField
        fullWidth
        label="Error Message"
        size="small"
        value={getOptionValue('errorMessage')}
        onChange={(e) => handleChange('errorMessage', e.target.value)}
        helperText="Message to show when no option is selected"
        sx={{ mb: 2 }}
      />
      <FormControlLabel
        control={
          <Switch
            checked={question.allowOther || false}
            onChange={(e) => {
              onChange('allowOther', e.target.checked);
              if (e.target.checked) {
                const newOptions = [...(question.choices || []), 'Other'];
                onChange('choices', newOptions);
              } else {
                const newOptions = (question.choices || []).filter(opt => opt !== 'Other');
                onChange('choices', newOptions);
              }
            }}
          />
        }
        label="Allow 'Other' option"
      />
      <FormControlLabel
        control={
          <Switch
            checked={question.randomize || false}
            onChange={(e) => handleChange('randomize', e.target.checked)}
          />
        }
        label="Randomize options"
      />
    </Box>
  );

  const handleLogicRuleChange = (rules) => {
    // Update the question with new logic rules
    onChange('logicRules', rules);
    onChange('logicRulesCount', rules.length);
    onChange('hasLogicRules', rules.length > 0);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Question Options
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Tab Navigation - Logic tab removed */}
      <Tabs
        value={activeTab}
        onChange={(e, value) => setActiveTab(value)}
        sx={{ mb: 2 }}
        variant="fullWidth"
      >
        <Tab label="General" value="general" />
      </Tabs>

      <Divider sx={{ mb: 2 }} />

      {/* Tab Content - Validation moved to General, Logic tab removed */}
      {activeTab === 'general' && (
        <>
          {commonOptions}

          {/* Validation Options - Now in General tab */}
          {['short-answer', 'long-answer', 'number'].includes(question.type) && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Validation</Typography>
              {textOptions}
            </Box>
          )}

          {/* Render specific options based on question type */}
          {['multiple-choice', 'checkboxes', 'dropdown'].includes(question.type) && multipleChoiceOptions}
        </>
      )}
    </Box>
  );
};

export default QuestionOptions;